import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Icon } from '../ui';
import { useUploadSheet } from '../routes/landlord/parts/uploadStore';

/** A drag carries files (not text or a link dragged inside the page). */
const hasFiles = (e: DragEvent) => !!e.dataTransfer && Array.from(e.dataTransfer.types).includes('Files');

/**
 * "+ Beleg": the one action every landlord page offers (top bar on wide screens, an icon in the small-screen bar).
 * Opens the global upload sheet, inside a property with that property preselected.
 */
export function QuickUploadButton({ propertyId, compact }: { propertyId?: string | null; compact?: boolean }) {
  const { t } = useTranslation();
  const show = useUploadSheet((s) => s.show);
  const open = () => show({ propertyId: propertyId ?? undefined });
  if (compact)
    return (
      <button type="button" className="lt-menu lt-upload" aria-label={t('workspace.upload.aria')} onClick={open}>
        <Icon name="upload" size={20} strokeWidth={2} />
      </button>
    );
  return (
    <Button size="sm" icon="plus" onClick={open} aria-label={t('workspace.upload.aria')}>
      {t('workspace.upload.button')}
    </Button>
  );
}

/**
 * Files dragged anywhere over the landlord frame: a calm hint ("Loslassen zum Hochladen"), dropping opens the upload
 * sheet with the files (and the current property). Drop areas of the page keep their own handling: a drop they
 * handled (default prevented) is ignored here, and the hint never takes pointer events away from them. While the
 * sheet is open its own drop area takes the files.
 */
export function FileDropLayer({ propertyId, enabled }: { propertyId?: string | null; enabled: boolean }) {
  const { t } = useTranslation();
  const [over, setOver] = useState(false);
  const depth = useRef(0);
  const pid = useRef(propertyId);
  pid.current = propertyId;
  useEffect(() => {
    if (!enabled) return;
    const reset = () => {
      depth.current = 0;
      setOver(false);
    };
    const onEnter = (e: DragEvent) => {
      if (!hasFiles(e) || useUploadSheet.getState().open) return;
      depth.current += 1;
      setOver(true);
    };
    const onLeave = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      depth.current = Math.max(0, depth.current - 1);
      if (depth.current === 0) setOver(false);
    };
    const onOver = (e: DragEvent) => {
      // allow dropping anywhere (else the browser opens the file and leaves the app)
      if (hasFiles(e)) e.preventDefault();
    };
    const onDrop = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      reset();
      // a drop area of the page (DropZone, FileDrop, the sheet) handled it already
      if (e.defaultPrevented) return;
      e.preventDefault();
      const { open, show } = useUploadSheet.getState();
      if (open) return;
      const files = Array.from(e.dataTransfer?.files ?? []);
      if (files.length) show({ propertyId: pid.current ?? undefined, files });
    };
    window.addEventListener('dragenter', onEnter);
    window.addEventListener('dragleave', onLeave);
    window.addEventListener('dragover', onOver);
    window.addEventListener('drop', onDrop);
    window.addEventListener('dragend', reset);
    return () => {
      window.removeEventListener('dragenter', onEnter);
      window.removeEventListener('dragleave', onLeave);
      window.removeEventListener('dragover', onOver);
      window.removeEventListener('drop', onDrop);
      window.removeEventListener('dragend', reset);
    };
  }, [enabled]);
  if (!over) return null;
  return (
    <div className="file-drop-layer" aria-hidden>
      <div className="file-drop-hint">
        <Icon name="upload" size={28} strokeWidth={2} />
        <span>{t('workspace.upload.dropHint')}</span>
      </div>
    </div>
  );
}
